import { useEffect, useMemo, useState } from "react";
import type {
  Cue,
  Id,
  NoteColumnDef,
  RestoreArchiveMode,
  Scene,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  formatArchiveOccurrenceLabel,
  getSceneCues,
  listArchivedVersions,
  VAULT_RETENTION_DAYS,
} from "@supermanus/document-model";
import { DualColumnScript } from "./DualColumnScript";
import { DurationField } from "./DurationField";
import type { RowRuleMode, ScriptColumnOrder } from "./editorSettings";
import { RestoreArchiveDialog } from "./RestoreArchiveDialog";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";
import { SceneHeadingLine } from "./SceneHeadingLine";

function formatArchivedAt(iso?: string): string {
  if (!iso) return "Ukendt tidspunkt";
  try {
    return new Date(iso).toLocaleString("da-DK", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const noopFocus = (_cueId: Id, _column: ScriptColumn) => {};
const noopChange = (_cueId: Id, _column: ScriptColumn, _content: string) => {};
const noopMove = (_cueId: Id, _before: Id | null) => {};
const noopNewPair = () => null;
const noopDelete = (_cueId: Id) => {};
const noopSpan = (_cueId: Id, _span: number) => {};
const noopToggle = (_cueId: Id) => {};

export function SceneArchiveVersionsDialog({
  document,
  liveSceneId,
  userId,
  noteColumns,
  defaultSceneHeaderBg,
  showColumnRule = true,
  rowRuleMode = "custom",
  rowRuleHitInGutter = true,
  columnOrder = "visual-audio",
  showSetupNumbers = false,
  showCameraNotes = false,
  showPosition = false,
  showHours = true,
  showFrames = false,
  fps = 25,
  onCancel,
  onRestore,
}: {
  document: ScriptDocument;
  liveSceneId: Id;
  userId: Id;
  noteColumns: NoteColumnDef[];
  defaultSceneHeaderBg: string;
  showColumnRule?: boolean;
  rowRuleMode?: RowRuleMode;
  rowRuleHitInGutter?: boolean;
  columnOrder?: ScriptColumnOrder;
  showSetupNumbers?: boolean;
  showCameraNotes?: boolean;
  showPosition?: boolean;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
  onCancel: () => void;
  onRestore: (
    archivedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
}) {
  const versions = useMemo(
    () => listArchivedVersions(document, liveSceneId),
    [document, liveSceneId],
  );
  const live = document.scenes[liveSceneId];
  const [selectedId, setSelectedId] = useState<Id | null>(
    versions[versions.length - 1]?.id ?? null,
  );
  const [restoreId, setRestoreId] = useState<Id | null>(null);

  useEffect(() => {
    if (versions.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !versions.some((v) => v.id === selectedId)) {
      setSelectedId(versions[versions.length - 1]!.id);
    }
  }, [versions, selectedId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (restoreId) setRestoreId(null);
        else onCancel();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, restoreId]);

  const selected: Scene | null = selectedId
    ? (document.scenes[selectedId] ?? null)
    : null;
  const cues: Cue[] = selected
    ? getSceneCues(document, selected.id)
    : [];
  const selectedIndex = selected
    ? versions.findIndex((v) => v.id === selected.id)
    : -1;
  const occurrenceLabel =
    selected && selectedIndex >= 0
      ? formatArchiveOccurrenceLabel(selected, selectedIndex + 1)
      : "";

  const liveName = live?.title.trim() || "Unavngiven scene";
  const restoreTarget = restoreId ? document.scenes[restoreId] : null;
  const restoreIndex = restoreTarget
    ? versions.findIndex((v) => v.id === restoreTarget.id)
    : -1;

  return (
    <div
      className="sm-dialog-backdrop sm-archive-browse-backdrop"
      role="presentation"
      onClick={onCancel}
    >
      <div
        className="sm-dialog sm-archive-browse-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-archive-browse-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-archive-browse-header">
          <h2 id="sm-archive-browse-title">Arkiverede versioner</h2>
          <p>
            Forekomster af scene {live?.number ?? "?"} «{liveName}». Vælg én
            for at læse den, eller gendan den til manus (scenens rigtige titel
            bevares). Versioner slettes automatisk efter {VAULT_RETENTION_DAYS}{" "}
            dage.
          </p>
        </div>

        {versions.length === 0 ? (
          <p className="sm-dialog-hint">Ingen arkiverede versioner.</p>
        ) : (
          <div className="sm-archive-browse-body">
            <ul className="sm-archive-browse-list" role="listbox">
              {versions.map((v, i) => {
                const label = formatArchiveOccurrenceLabel(v, i + 1);
                const custom = v.archiveLabel?.trim();
                return (
                  <li key={v.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={v.id === selectedId}
                      className={
                        v.id === selectedId
                          ? "sm-archive-browse-item is-selected"
                          : "sm-archive-browse-item"
                      }
                      onClick={() => setSelectedId(v.id)}
                    >
                      <strong>{label}</strong>
                      <span className="sm-dialog-hint">
                        {custom
                          ? `Scenetitel: ${v.title.trim() || "Unavngiven scene"} · `
                          : ""}
                        {formatArchivedAt(v.archivedAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="sm-archive-browse-preview">
              {selected ? (
                <>
                  <div className="sm-archive-browse-preview-meta">
                    <strong>{occurrenceLabel}</strong>
                    <span className="sm-dialog-hint">
                      Scenetitel: {selected.title.trim() || "Unavngiven scene"}
                    </span>
                  </div>
                  <div
                    className="sm-script-scene-head"
                    style={{
                      backgroundColor: sceneHeaderBg(
                        selected.headerBackground,
                        defaultSceneHeaderBg,
                      ),
                    }}
                  >
                    <div className="sm-script-scene-head-row">
                      <div className="sm-script-scene-head-main">
                        <SceneHeadingLine
                          sceneNumber={selected.number}
                          heading={selected.heading}
                          headingKind={selected.headingKind}
                          headingLocation={selected.headingLocation}
                          headingTime={selected.headingTime}
                          headingNote={selected.headingNote}
                          readOnly
                          onChange={() => {}}
                        />
                        <strong className="sm-script-scene-title">
                          {selected.title.trim() || "Unavngiven scene"}
                        </strong>
                      </div>
                      <DurationField
                        className="sm-script-scene-duration"
                        valueSeconds={selected.durationSeconds}
                        onChange={() => {}}
                        readOnly
                        showHours={showHours}
                        showFrames={showFrames}
                        fps={fps}
                        ariaLabel={`Varighed for arkiveret scene`}
                      />
                    </div>
                  </div>
                  <div className="sm-archive-browse-script">
                    <DualColumnScript
                      cues={cues}
                      userId={userId}
                      readOnly
                      showColumnRule={showColumnRule}
                      rowRuleMode={rowRuleMode}
                      rowRuleHitInGutter={rowRuleHitInGutter}
                      castMembers={document.production.castMembers ?? []}
                      comments={Object.values(document.comments ?? {}).filter(
                        (c) => selected.cueIds.includes(c.cueId),
                      )}
                      showComments
                      sceneNumber={selected.number}
                      showSetupNumbers={showSetupNumbers}
                      showCameraNotes={showCameraNotes}
                      showPosition={showPosition}
                      columnOrder={columnOrder}
                      noteColumns={noteColumns}
                      onFocus={noopFocus}
                      onBlur={noopFocus}
                      onChange={noopChange}
                      onMoveCueBlock={noopMove}
                      onNewPair={noopNewPair}
                      onDeletePair={noopDelete}
                      onSetVisualSpan={noopSpan}
                      onSetAudioSpan={noopSpan}
                      onTogglePrintBorderBelow={noopToggle}
                    />
                  </div>
                </>
              ) : (
                <p className="sm-dialog-hint">Vælg en version i listen.</p>
              )}
            </div>
          </div>
        )}

        <div className="sm-dialog-actions">
          <button type="button" onClick={onCancel}>
            Luk
          </button>
          <button
            type="button"
            className="sm-dialog-primary"
            disabled={!selectedId}
            onClick={() => selectedId && setRestoreId(selectedId)}
          >
            Gendan…
          </button>
        </div>
      </div>

      {restoreId && restoreTarget ? (
        <RestoreArchiveDialog
          sceneLabel={
            restoreIndex >= 0
              ? `«${formatArchiveOccurrenceLabel(restoreTarget, restoreIndex + 1)}»`
              : "den arkiverede version"
          }
          onCancel={() => setRestoreId(null)}
          onConfirm={(mode) => {
            const id = restoreId;
            setRestoreId(null);
            onRestore(id, { mode });
            onCancel();
          }}
        />
      ) : null}
    </div>
  );
}
